import { auth } from 'data/firestore/firebase';
import {
    Invoice,
    InvoiceBuilderImageFooterBlock,
    InvoiceImageWidth,
} from './types';

export const invoiceDownloadFileName = (invoice: Invoice) => {
    const title = invoice.document.design.labels.documentTitle || 'Invoice';
    const raw = [title, invoice.number].filter(Boolean).join(' ').trim();
    return `${raw.replace(/[/\\?%*:|"<>]/g, '-')}.pdf`;
};

export const invoiceImageUrls = (invoice: Invoice) => {
    const { logo, footerBlocks } = invoice.document.design;
    const urls = [
        logo.style === 'image' ? logo.imageUrl : '',
        ...(footerBlocks || []).map((block) => (
            block.kind === 'image' ? block.imageUrl : ''
        )),
    ].filter((url) => url && !url.startsWith('data:'));
    return urls.filter((url, index) => urls.indexOf(url) === index);
};

const blobToDataUrl = (blob: Blob) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error || new Error('Could not read image'));
    reader.readAsDataURL(blob);
});

const blobToBytes = (blob: Blob) => new Promise<Uint8Array>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error || new Error('Could not read image'));
    reader.readAsArrayBuffer(blob);
});

const sniffImageType = async (blob: Blob) => {
    if (blob.type && blob.type.startsWith('image/')) return blob.type;
    const bytes = await blobToBytes(blob.slice(0, 12));
    if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) {
        return 'image/png';
    }
    if (bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) {
        return 'image/jpeg';
    }
    if (bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) {
        return 'image/webp';
    }
    return 'image/png';
};

const createStorageProxyUrl = (url: string) => {
    const proxyUrl = new URL('/storage-proxy', process.env.REACT_APP_BASE_URL as string);
    proxyUrl.searchParams.set('url', url);
    return proxyUrl.toString();
};

const fetchImageBlob = async (url: string) => {
    const headers: Record<string, string> = {};
    if (auth.currentUser) {
        headers.Authorization = `Bearer ${await auth.currentUser.getIdToken()}`;
    }
    const response = await fetch(createStorageProxyUrl(url), { headers });
    if (!response.ok) {
        throw new Error(`Could not load invoice image (${response.status})`);
    }
    const blob = await response.blob();
    if (!blob.size) {
        throw new Error('Could not load invoice image');
    }
    return blob;
};

export const urlToDataUrl = async (url: string) => {
    const blob = await fetchImageBlob(url);
    const type = await sniffImageType(blob);
    const imageBlob = blob.type === type ? blob : new Blob([blob], { type });
    const dataUrl = await blobToDataUrl(imageBlob);
    if (!dataUrl.startsWith('data:image/')) {
        throw new Error('Could not load invoice image');
    }
    return dataUrl;
};

export interface InvoiceImageSize {
    width: number;
    height: number;
}

const imageWidthRatio = (size: InvoiceImageWidth) => {
    if (size === 'small') return 1 / 4;
    if (size === 'medium') return 1 / 3;
    if (size === 'large') return 1 / 2;
    return 1;
};

export const invoiceImageWidth = (
    containerWidth: number,
    size: InvoiceImageWidth,
) => containerWidth * imageWidthRatio(size);

export const footerImageBox = (
    naturalWidth: number,
    naturalHeight: number,
    containerWidth: number,
    size?: InvoiceImageWidth,
): InvoiceImageSize | undefined => {
    if (naturalWidth <= 0 || naturalHeight <= 0) return undefined;
    if (size) {
        if (containerWidth <= 0) return undefined;
        const width = invoiceImageWidth(containerWidth, size);
        return {
            width,
            height: naturalHeight * (width / naturalWidth),
        };
    }
    if (naturalWidth <= containerWidth) {
        return { width: naturalWidth, height: naturalHeight };
    }
    const scale = containerWidth / naturalWidth;
    return {
        width: containerWidth,
        height: naturalHeight * scale,
    };
};

const measureImageNaturalSize = (src: string) => (
    new Promise<InvoiceImageSize>((resolve, reject) => {
        const image = new Image();
        image.onload = () => {
            if (image.naturalWidth <= 0 || image.naturalHeight <= 0) {
                reject(new Error('Could not measure image'));
                return;
            }
            resolve({ width: image.naturalWidth, height: image.naturalHeight });
        };
        image.onerror = () => reject(new Error('Could not measure image'));
        image.src = src;
    })
);

export const fitImageBox = (
    naturalWidth: number,
    naturalHeight: number,
    maxWidth: number,
    maxHeight: number,
): InvoiceImageSize | undefined => {
    if (naturalWidth <= 0 || naturalHeight <= 0) return undefined;
    const scale = Math.min(1, maxWidth / naturalWidth, maxHeight / naturalHeight);
    return {
        width: naturalWidth * scale,
        height: naturalHeight * scale,
    };
};

export const measureInvoiceImageSizes = async (
    invoice: Invoice,
    extraUrls: string[] = [],
) => {
    const { logo, footerBlocks } = invoice.document.design;
    const urls = [
        logo.style === 'image' ? logo.imageUrl : '',
        ...(footerBlocks || [])
            .filter((block): block is InvoiceBuilderImageFooterBlock => (
                block.kind === 'image' && Boolean(block.imageUrl)
            ))
            .map((block) => block.imageUrl),
        ...extraUrls,
    ].filter(Boolean);
    const unique = urls.filter((url, index) => urls.indexOf(url) === index);
    const sizes: Record<string, InvoiceImageSize> = {};
    await Promise.all(unique.map(async (url) => {
        try {
            sizes[url] = await measureImageNaturalSize(url);
        } catch {
            // Keep file-size fallback in the document when measure fails.
        }
    }));
    return sizes;
};

export const invoiceWithInlinedImages = async (invoice: Invoice): Promise<Invoice> => {
    const urls = invoiceImageUrls(invoice);
    if (urls.length === 0) return invoice;

    const replacements = await Promise.all(urls.map(async (url) => {
        try {
            return [url, await urlToDataUrl(url)] as const;
        } catch {
            return [url, url] as const;
        }
    }));
    const map = new Map(replacements);
    const inline = (url: string) => map.get(url) || url;
    const { design } = invoice.document;

    return {
        ...invoice,
        document: {
            ...invoice.document,
            design: {
                ...design,
                logo: {
                    ...design.logo,
                    imageUrl: inline(design.logo.imageUrl),
                },
                footerBlocks: (design.footerBlocks || []).map((block) => (
                    block.kind === 'image'
                        ? { ...block, imageUrl: inline(block.imageUrl) }
                        : block
                )),
            },
        },
    };
};
